import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Full Screen Modal Component
const FullScreenModal = ({ isOpen, onClose, title, content, copyText }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const copyToClipboard = () => {
    if (copyText) {
      navigator.clipboard.writeText(copyText);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <div className="flex items-center space-x-2">
            {copyText && (
              <button
                onClick={copyToClipboard}
                className="px-3 py-1 text-sm bg-stone-500 text-white rounded hover:bg-stone-600 transition-colors shadow-sm"
              >
                Copy
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-auto p-4">
          <div className="w-full h-full">
            {content}
          </div>
        </div>
        
        <div className="p-4 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">Press ESC to close • Use scroll to navigate large content</p>
        </div>
      </div>
    </div>
  );
};

// Simplified Text Formatter Component with Full Screen Support
const TextFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const sampleText = `   This   is    a   sample   text   with   
  irregular     spacing  and  

  multiple line breaks.
  
  SOME WORDS ARE IN CAPS   and   some   are   in   lowercase.
  
  
  There are also trailing spaces at the end of lines.   
  And some lines have    extra    spaces    between    words.  `;
  
  const processText = () => {
    if (!input.trim()) {
      setOutput('');
      return;
    }

    try {
      // Simple text formatting: clean spaces and normalize line breaks
      const result = input
        .replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space
        .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple line breaks with double
        .replace(/^\s+|\s+$/gm, '') // Trim each line
        .replace(/^\n+|\n+$/g, ''); // Trim start and end newlines
      
      setOutput(result);
    } catch (err) {
      setOutput('Error: ' + err.message);
    }
  };

  const loadSample = () => {
    setInput(sampleText);
    setOutput('');
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  const swapInputOutput = () => {
    const temp = input;
    setInput(output);
    setOutput(temp);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="w-full px-4 mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">Text Formatter</h1>
            <p className="text-gray-600 text-sm">Clean and format text content</p>
          </div>

          <div className="flex justify-center gap-4 mb-8">
            <button 
              onClick={processText}
              disabled={!input.trim()}
              className="px-6 py-3 bg-stone-500 text-white rounded-lg hover:bg-stone-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium shadow-sm"
            >
              Format Text
            </button>
            <button onClick={loadSample} className="px-4 py-2 bg-stone-400 text-white rounded-lg hover:bg-stone-500 transition-colors duration-200 text-sm font-medium shadow-sm">Load Sample</button>
            <button onClick={swapInputOutput} disabled={!output} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium shadow-sm">Swap</button>
            <button onClick={clearAll} className="px-4 py-2 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm">Clear All</button>
          </div>

          <div className="flex gap-6">
            <div className="w-1/4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Input Text</label>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Enter your text here..."
                  className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-amber-200 bg-amber-50 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm"
                />
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">{input.length} characters • {input.split('\n').length} lines</p>
              </div>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Formatted Output
                  </label>
                  {output && (
                    <button
                      onClick={() => setIsFullScreenOpen(true)}
                      className="inline-flex items-center px-3 py-1.5 text-xs bg-stone-600 text-white rounded-md hover:bg-stone-700 transition-colors duration-200"
                      title="Expand to full screen"
                    >
                      <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      Expand
                    </button>
                  )}
                </div>
                <textarea
                  value={output}
                  readOnly
                  placeholder="Formatted text will appear here..."
                  className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-stone-200 rounded-lg shadow-sm bg-stone-50 resize-none font-mono text-sm"
                />
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">{output.length} characters • {output.split('\n').length} lines</p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 max-w-2xl mx-auto">
              <h3 className="text-stone-800 font-medium mb-2">📝 Text Formatting</h3>
              <p className="text-stone-700 text-sm">
                This tool cleans your text by removing extra spaces, normalizing line breaks, and trimming whitespace. 
                Perfect for cleaning up copied text from various sources.
              </p>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        title="Text Formatter - Formatted Output"
        copyText={output}
        content={
          <textarea
            value={output}
            readOnly
            className="w-full h-full resize-none font-mono text-sm p-4 border-none focus:outline-none bg-gray-50"
          />
        }
      />

      <footer className="py-6 text-center">
        <p className="text-xs text-gray-500">Clean and format text content by removing extra spaces and normalizing line breaks</p>
      </footer>
    </div>
  );
};

// JSON Diff Component with Full Screen Support 
const JSONDiff = () => {
  const [leftInput, setLeftInput] = useState('');
  const [rightInput, setRightInput] = useState('');
  const [leftParsed, setLeftParsed] = useState(null);
  const [rightParsed, setRightParsed] = useState(null);
  const [leftError, setLeftError] = useState('');
  const [rightError, setRightError] = useState('');
  const [diffResult, setDiffResult] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [leftFormattedDiff, setLeftFormattedDiff] = useState([]);
  const [rightFormattedDiff, setRightFormattedDiff] = useState([]);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const sampleLeft = {
    "name": "John Doe",
    "age": 30,
    "address": {
      "street": "123 Main St",
      "city": "New York",
      "zipCode": "10001"
    },
    "hobbies": ["reading", "swimming"],
    "isActive": true,
    "salary": 50000
  };

  const sampleRight = {
    "name": "John Smith",
    "age": 32,
    "address": {
      "street": "456 Oak Ave",
      "city": "New York",
      "zipCode": "10001"
    },
    "hobbies": ["reading", "swimming", "coding"],
    "isActive": true,
    "department": "Engineering"
  };

  // Deep comparison function
  const deepCompare = (obj1, obj2, path = '') => {
    const result = {
      added: [],
      removed: [],
      modified: [],
      unchanged: []
    };

    if (obj1 === null && obj2 === null) {
      result.unchanged.push({ path: path || 'root', value: null });
      return result;
    }
    
    if (obj1 === null || obj1 === undefined) {
      result.added.push({ path: path || 'root', value: obj2 });
      return result;
    }
    
    if (obj2 === null || obj2 === undefined) {
      result.removed.push({ path: path || 'root', value: obj1 });
      return result;
    }

    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      const maxLength = Math.max(obj1.length, obj2.length);
      
      for (let i = 0; i < maxLength; i++) {
        const currentPath = path ? `${path}[${i}]` : `[${i}]`;
        
        if (i >= obj1.length) {
          result.added.push({ path: currentPath, value: obj2[i] });
        } else if (i >= obj2.length) {
          result.removed.push({ path: currentPath, value: obj1[i] });
        } else {
          const nestedResult = deepCompare(obj1[i], obj2[i], currentPath);
          result.added.push(...nestedResult.added);
          result.removed.push(...nestedResult.removed);
          result.modified.push(...nestedResult.modified);
          result.unchanged.push(...nestedResult.unchanged);
        }
      }
      
      return result;
    }

    if (typeof obj1 === 'object' && typeof obj2 === 'object' && obj1 !== null && obj2 !== null) {
      if (Array.isArray(obj1) || Array.isArray(obj2)) {
        result.modified.push({ path: path || 'root', oldValue: obj1, newValue: obj2 });
        return result;
      }

      const allKeys = new Set([
        ...Object.keys(obj1),
        ...Object.keys(obj2)
      ]);

      allKeys.forEach(key => {
        const currentPath = path ? `${path}.${key}` : key;
        const val1 = obj1[key];
        const val2 = obj2[key];

        if (!(key in obj1)) {
          result.added.push({ path: currentPath, value: val2 });
        } else if (!(key in obj2)) {
          result.removed.push({ path: currentPath, value: val1 });
        } else {
          const nestedResult = deepCompare(val1, val2, currentPath);
          result.added.push(...nestedResult.added);
          result.removed.push(...nestedResult.removed);
          result.modified.push(...nestedResult.modified);
          result.unchanged.push(...nestedResult.unchanged);
        }
      });

      return result;
    }

    if (obj1 !== obj2) {
      result.modified.push({ path: path || 'root', oldValue: obj1, newValue: obj2 });
    } else {
      result.unchanged.push({ path: path || 'root', value: obj1 });
    }

    return result;
  };

  // Format JSON with diff highlighting
  const formatJSONWithDiff = (obj, diffResult, side = 'left') => {
    if (!obj || !diffResult) return JSON.stringify(obj, null, 2);
    
    let formatted = JSON.stringify(obj, null, 2);
    const lines = formatted.split('\n');
    const coloredLines = [];

    const pathToLine = {};
    const pathStack = [];
    const getPath = (level) => pathStack.slice(0, level + 1).filter(Boolean).join('.');

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      const indentLevel = Math.floor((line.length - line.trimStart().length) / 2);
      
      pathStack.length = indentLevel;
      
      if (trimmed.includes(':')) {
        const keyMatch = trimmed.match(/"([^"]+)":/);
        if (keyMatch) {
          const key = keyMatch[1];
          pathStack[indentLevel] = key;
          const fullPath = getPath(indentLevel + 1);
          pathToLine[fullPath] = index;
        }
      }
    });

    lines.forEach((line, index) => {
      let processedLine = line;
      let isDiff = false;
      
      const linePath = Object.keys(pathToLine).find(path => pathToLine[path] === index);
      if (linePath) {
        const trimmed = line.trim();
        
        if (side === 'left') {
          const removedItem = diffResult.removed.find(item => item.path === linePath);
          if (removedItem) {
            processedLine = line.replace(trimmed, `<span class="text-red-700 bg-red-100 px-1 rounded font-medium">− ${trimmed}</span>`);
            isDiff = true;
          } else {
            const modifiedItem = diffResult.modified.find(item => item.path === linePath);
            if (modifiedItem) {
              processedLine = line.replace(trimmed, `<span class="text-orange-700 bg-orange-100 px-1 rounded font-medium">~ ${trimmed}</span>`);
              isDiff = true;
            }
          }
        } else {
          const addedItem = diffResult.added.find(item => item.path === linePath);
          if (addedItem) {
            processedLine = line.replace(trimmed, `<span class="text-green-700 bg-green-100 px-1 rounded font-medium">+ ${trimmed}</span>`);
            isDiff = true;
          } else {
            const modifiedItem = diffResult.modified.find(item => item.path === linePath);
            if (modifiedItem) {
              processedLine = line.replace(trimmed, `<span class="text-orange-700 bg-orange-100 px-1 rounded font-medium">~ ${trimmed}</span>`);
              isDiff = true;
            }
          }
        }
      }
      
      coloredLines.push({ 
        line: processedLine, 
        index, 
        isDiff,
        originalLine: line
      });
    });

    return coloredLines;
  };

  const compareJSON = () => {
    try {
      const parsed1 = leftInput.trim() ? JSON.parse(leftInput) : {};
      const parsed2 = rightInput.trim() ? JSON.parse(rightInput) : {};
      
      setLeftParsed(parsed1);
      setRightParsed(parsed2);
      setLeftError('');
      setRightError('');
      
      const diff = deepCompare(parsed1, parsed2);
      setDiffResult(diff);
      setShowDiff(true);
      setIsComparing(true);
      
      const leftDiff = formatJSONWithDiff(parsed1, diff, 'left');
      const rightDiff = formatJSONWithDiff(parsed2, diff, 'right');
      
      setLeftFormattedDiff(leftDiff);
      setRightFormattedDiff(rightDiff);
      
    } catch (err) {
      try {
        JSON.parse(leftInput);
        setLeftError('');
      } catch (leftErr) {
        setLeftError('Invalid JSON: ' + leftErr.message);
      }
      
      try {
        JSON.parse(rightInput);
        setRightError('');
      } catch (rightErr) {
        setRightError('Invalid JSON: ' + rightErr.message);
      }
      
      setDiffResult(null);
      setShowDiff(false);
    }
  };

  const resetComparison = () => {
    setIsComparing(false);
    setShowDiff(false);
    setDiffResult(null);
    setLeftFormattedDiff([]);
    setRightFormattedDiff([]);
  };

  const loadSample = () => {
    setLeftInput(JSON.stringify(sampleLeft, null, 2));
    setRightInput(JSON.stringify(sampleRight, null, 2));
    resetComparison();
  };

  const clearAll = () => {
    setLeftInput('');
    setRightInput('');
    resetComparison();
  };

  const getDiffText = () => {
    const leftText = leftFormattedDiff.map(item => item.originalLine).join('\n');
    const rightText = rightFormattedDiff.map(item => item.originalLine).join('\n');
    return `--- Left JSON\n${leftText}\n\n+++ Right JSON\n${rightText}`;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="w-full px-4 mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">JSON Diff</h1>
            <p className="text-gray-600 text-sm">Compare JSON objects and see differences highlighted in the input boxes</p>
          </div>

          <div className="flex justify-center gap-4 mb-8">
            {!isComparing ? (
              <>
                <button
                  onClick={compareJSON}
                  disabled={!leftInput.trim() || !rightInput.trim()}
                  className="px-6 py-3 bg-stone-500 text-white rounded-lg hover:bg-stone-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Compare JSON
                </button>
                <button
                  onClick={loadSample}
                  className="px-4 py-3 bg-stone-400 text-white rounded-lg hover:bg-stone-500 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Load Sample
                </button>
                <button
                  onClick={clearAll}
                  className="px-4 py-3 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Clear All
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={resetComparison}
                  className="px-6 py-3 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Reset & Edit Again
                </button>
                <button
                  onClick={() => setIsFullScreenOpen(true)}
                  className="inline-flex items-center px-4 py-3 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  Full Screen Diff
                </button>
                <button
                  onClick={clearAll}
                  className="px-4 py-3 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  Clear All
                </button>
              </>
            )}
          </div>

          {isComparing && diffResult && (
            <div className="mb-6 text-center">
              <div className="inline-flex items-center space-x-4 bg-blue-50 border border-blue-200 rounded-lg px-6 py-3">
                <span className="text-blue-800 font-medium">Comparison Results:</span>
                <span className="text-green-600 font-semibold">{diffResult.added.length} Added</span>
                <span className="text-red-600 font-semibold">{diffResult.removed.length} Removed</span>
                <span className="text-orange-600 font-semibold">{diffResult.modified.length} Modified</span>
                <span className="text-gray-600 font-semibold">{diffResult.unchanged.length} Unchanged</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Left JSON (Original)
                  {isComparing && (
                    <span className="ml-2 inline-block bg-stone-100 text-stone-800 text-xs px-2 py-1 rounded-full">
                      Comparison Mode - Click Reset to Edit
                    </span>
                  )}
                </label>
                <div className="relative">
                  {!isComparing ? (
                    <textarea
                      value={leftInput}
                      onChange={(e) => setLeftInput(e.target.value)}
                      placeholder="Enter your first JSON object here..."
                      className={`w-full h-[calc(100vh-320px)] px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm ${
                        leftError ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'
                      }`}
                    />
                  ) : (
                    <div className="w-full h-[calc(100vh-320px)] border-2 border-stone-300 rounded-lg shadow-lg bg-stone-50 overflow-auto font-mono text-sm relative">
                      <div className="absolute top-2 right-2 bg-stone-200 text-stone-800 text-xs px-2 py-1 rounded-full font-medium">
                        {diffResult ? 
                          `${diffResult.removed.length} removed, ${diffResult.modified.length} modified` :
                          'Comparison View'
                        }
                      </div>
                      <div className="p-4 pt-8">
                        {leftFormattedDiff.map((item, index) => (
                          <div 
                            key={index} 
                            className="whitespace-pre leading-relaxed py-1 transition-all duration-200 hover:bg-stone-100"
                            dangerouslySetInnerHTML={{ __html: item.line }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {leftError && (
                    <div className="absolute top-2 right-2 text-red-600 text-xs bg-red-100 px-2 py-1 rounded">
                      Error
                    </div>
                  )}
                </div>
                {leftError && (
                  <p className="text-red-600 text-sm mt-1">{leftError}</p>
                )}
                <div className="text-center">
                  <p className="text-gray-500 text-xs">
                    {leftInput.length} characters
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Right JSON (Comparison)
                  {isComparing && (
                    <span className="ml-2 inline-block bg-stone-100 text-stone-800 text-xs px-2 py-1 rounded-full">
                      Comparison Mode - Click Reset to Edit
                    </span>
                  )}
                </label>
                <div className="relative">
                  {!isComparing ? (
                    <textarea
                      value={rightInput}
                      onChange={(e) => setRightInput(e.target.value)}
                      placeholder="Enter your second JSON object here..."
                      className={`w-full h-[calc(100vh-320px)] px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm ${
                        rightError ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'
                      }`}
                    />
                  ) : (
                    <div className="w-full h-[calc(100vh-320px)] border-2 border-stone-300 rounded-lg shadow-lg bg-stone-50 overflow-auto font-mono text-sm relative">
                      <div className="absolute top-2 right-2 bg-stone-200 text-stone-800 text-xs px-2 py-1 rounded-full font-medium">
                        {diffResult ? 
                          `${diffResult.added.length} added, ${diffResult.modified.length} modified` :
                          'Comparison View'
                        }
                      </div>
                      <div className="p-4 pt-8">
                        {rightFormattedDiff.map((item, index) => (
                          <div 
                            key={index} 
                            className="whitespace-pre leading-relaxed py-1 transition-all duration-200 hover:bg-stone-100"
                            dangerouslySetInnerHTML={{ __html: item.line }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {rightError && (
                    <div className="absolute top-2 right-2 text-red-600 text-xs bg-red-100 px-2 py-1 rounded">
                      Error
                    </div>
                  )}
                </div>
                {rightError && (
                  <p className="text-red-600 text-sm mt-1">{rightError}</p>
                )}
                <div className="text-center">
                  <p className="text-gray-500 text-xs">
                    {rightInput.length} characters
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Legend:</h3>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-2">
                <span className="text-green-700 font-mono text-sm bg-green-100 px-2 py-1 rounded">+ Added</span>
                <span className="text-sm text-gray-600">Properties only in right JSON</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-red-700 font-mono text-sm bg-red-100 px-2 py-1 rounded">− Removed</span>
                <span className="text-sm text-gray-600">Properties only in left JSON</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-orange-700 font-mono text-sm bg-orange-100 px-2 py-1 rounded">~ Modified</span>
                <span className="text-sm text-gray-600">Properties with different values</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        title="JSON Diff - Side by Side Comparison"
        copyText={getDiffText()}
        content={
          <div className="w-full h-full grid grid-cols-2 gap-4">
            <div className="bg-gray-50 overflow-auto font-mono text-sm border-r">
              <div className="sticky top-0 bg-red-100 text-red-800 text-sm px-4 py-2 font-medium border-b">
                Left JSON (Original)
              </div>
              <div className="p-4">
                {leftFormattedDiff.map((item, index) => (
                  <div 
                    key={index} 
                    className="whitespace-pre leading-relaxed py-1"
                    dangerouslySetInnerHTML={{ __html: item.line }}
                  />
                ))}
              </div>
            </div>
            <div className="bg-gray-50 overflow-auto font-mono text-sm">
              <div className="sticky top-0 bg-green-100 text-green-800 text-sm px-4 py-2 font-medium border-b">
                Right JSON (Comparison)
              </div>
              <div className="p-4">
                {rightFormattedDiff.map((item, index) => (
                  <div 
                    key={index} 
                    className="whitespace-pre leading-relaxed py-1"
                    dangerouslySetInnerHTML={{ __html: item.line }}
                  />
                ))}
              </div>
            </div>
          </div>
        }
      />

      <footer className="py-6 text-center">
        <p className="text-xs text-gray-500">
          Compare JSON objects with differences highlighted directly in the input boxes
        </p>
      </footer>
    </div>
  );
};

// JWT Tool Component with Full Screen Support
const JWTTool = () => {
  const [mode, setMode] = useState('decode');
  const [jwtInput, setJwtInput] = useState('');
  const [headerInput, setHeaderInput] = useState('');
  const [payloadInput, setPayloadInput] = useState('');
  const [secretInput, setSecretInput] = useState('');
  const [decodedResult, setDecodedResult] = useState(null);
  const [encodedResult, setEncodedResult] = useState('');
  const [error, setError] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const sampleJWT = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE3MDczMzU0MjIsImF1ZCI6InRlc3QtYXVkaWVuY2UiLCJpc3MiOiJ0ZXN0LWlzc3VlciJ9.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';

  const sampleHeader = {
    "alg": "HS256",
    "typ": "JWT"
  };

  const samplePayload = {
    "sub": "1234567890",
    "name": "John Doe",
    "iat": 1516239022,
    "exp": 1707335422,
    "aud": "test-audience",
    "iss": "test-issuer"
  };

  const base64UrlDecode = (str) => {
    try {
      let padded = str;
      while (padded.length % 4) {
        padded += '=';
      }
      padded = padded.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(padded);
      return JSON.parse(decoded);
    } catch (err) {
      throw new Error('Invalid base64 encoding');
    }
  };

  const base64UrlEncode = (obj) => {
    try {
      const str = JSON.stringify(obj);
      const base64 = btoa(str);
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    } catch (err) {
      throw new Error('Failed to encode object');
    }
  };

  const decodeJWT = (token) => {
    if (!token || !token.trim()) {
      setError('Please enter a JWT token');
      setDecodedResult(null);
      setTokenInfo(null);
      return;
    }

    try {
      const parts = token.trim().split('.');
      if (parts.length !== 3) {
        setError('Invalid JWT format. JWT should have 3 parts separated by dots.');
        setDecodedResult(null);
        setTokenInfo(null);
        return;
      }

      const [headerB64, payloadB64, signature] = parts;
      
      const header = base64UrlDecode(headerB64);
      const payload = base64UrlDecode(payloadB64);

      setDecodedResult({
        header,
        payload,
        signature
      });

      const now = Math.floor(Date.now() / 1000);
      const info = {
        algorithm: header.alg || 'Unknown',
        type: header.typ || 'Unknown',
        issued: payload.iat ? new Date(payload.iat * 1000).toISOString() : null,
        expires: payload.exp ? new Date(payload.exp * 1000).toISOString() : null,
        notBefore: payload.nbf ? new Date(payload.nbf * 1000).toISOString() : null,
        issuer: payload.iss || null,
        audience: payload.aud || null,
        subject: payload.sub || null,
        isExpired: payload.exp ? payload.exp < now : null,
        isValid: payload.nbf ? payload.nbf <= now : true
      };

      setTokenInfo(info);
      setError('');
    } catch (err) {
      setError('Failed to decode JWT: ' + err.message);
      setDecodedResult(null);
      setTokenInfo(null);
    }
  };

  const encodeJWT = () => {
    try {
      let header = headerInput.trim() ? JSON.parse(headerInput) : sampleHeader;
      let payload = payloadInput.trim() ? JSON.parse(payloadInput) : samplePayload;

      const headerB64 = base64UrlEncode(header);
      const payloadB64 = base64UrlEncode(payload);
      
      const data = headerB64 + '.' + payloadB64;
      const secret = secretInput || 'your-256-bit-secret';
      
      let hash = 0;
      const fullString = data + secret;
      for (let i = 0; i < fullString.length; i++) {
        const char = fullString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      
      const signature = btoa(hash.toString()).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      
      const jwt = `${headerB64}.${payloadB64}.${signature}`;
      setEncodedResult(jwt);
      setError('');
    } catch (err) {
      setError('Failed to encode JWT: ' + err.message);
      setEncodedResult('');
    }
  };

  // Manual decode function for button click
  const handleDecodeJWT = () => {
    if (mode === 'decode') {
      decodeJWT(jwtInput);
    }
  };

  // Manual encode function for button click
  const handleEncodeJWT = () => {
    if (mode === 'encode') {
      encodeJWT();
    }
  };

  const loadSampleJWT = () => {
    setJwtInput(sampleJWT);
    setError('');
  };

  const loadSampleData = () => {
    setHeaderInput(JSON.stringify(sampleHeader, null, 2));
    setPayloadInput(JSON.stringify(samplePayload, null, 2));
    setSecretInput('your-256-bit-secret');
    setError('');
  };

  const clearAll = () => {
    setJwtInput('');
    setHeaderInput('');
    setPayloadInput('');
    setSecretInput('');
    setDecodedResult(null);
    setEncodedResult('');
    setTokenInfo(null);
    setError('');
  };

  const toggleMode = () => {
    const newMode = mode === 'decode' ? 'encode' : 'decode';
    setMode(newMode);
    clearAll();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Not set';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getOutputContent = () => {
    if (mode === 'decode' && decodedResult) {
      return `Header:\n${JSON.stringify(decodedResult.header, null, 2)}\n\nPayload:\n${JSON.stringify(decodedResult.payload, null, 2)}\n\nSignature:\n${decodedResult.signature}`;
    } else if (mode === 'encode' && encodedResult) {
      return encodedResult;
    }
    return '';
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">JWT Tool</h1>
            <p className="text-gray-600 text-sm">Encode, decode and validate JSON Web Tokens</p>
          </div>

          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 rounded-full p-1 flex">
              <button
                onClick={() => mode !== 'decode' && toggleMode()}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  mode === 'decode'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🔓 Decode JWT
              </button>
              <button
                onClick={() => mode !== 'encode' && toggleMode()}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  mode === 'encode'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🔒 Encode JWT
              </button>
            </div>
          </div>

          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={mode === 'decode' ? loadSampleJWT : loadSampleData}
              className="px-4 py-3 bg-stone-400 text-white rounded-lg hover:bg-stone-500 transition-colors duration-200 text-sm font-medium shadow-sm"
            >
              Load Sample
            </button>
            <button
              onClick={clearAll}
              className="px-4 py-3 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm"
            >
              Clear All
            </button>
          </div>

          {error && (
            <div className="text-center mb-6">
              <p className="text-red-600 text-sm bg-red-50 inline-block px-4 py-2 rounded-lg">
                {error}
              </p>
            </div>
          )}

          {mode === 'decode' ? (
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  JWT Token
                </label>
                <textarea
                  value={jwtInput}
                  onChange={(e) => setJwtInput(e.target.value)}
                  placeholder="Paste your JWT token here..."
                  className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                />
                <div className="text-center mt-2">
                  <p className="text-gray-500 text-xs">
                    {jwtInput.length} characters
                  </p>
                </div>
                <div className="text-center mt-4">
                  <button
                    onClick={handleDecodeJWT}
                    disabled={!jwtInput.trim()}
                    className="px-6 py-3 bg-stone-500 text-white rounded-lg hover:bg-stone-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium shadow-sm"
                  >
                    🔓 Decode JWT
                  </button>
                </div>
              </div>

              {tokenInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-blue-800 mb-4">Token Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-blue-700">Algorithm:</span>
                      <span className="ml-2 text-sm text-blue-600">{tokenInfo.algorithm}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-blue-700">Type:</span>
                      <span className="ml-2 text-sm text-blue-600">{tokenInfo.type}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-blue-700">Issuer:</span>
                      <span className="ml-2 text-sm text-blue-600">{tokenInfo.issuer || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-blue-700">Subject:</span>
                      <span className="ml-2 text-sm text-blue-600">{tokenInfo.subject || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-blue-700">Audience:</span>
                      <span className="ml-2 text-sm text-blue-600">{tokenInfo.audience || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-blue-700">Status:</span>
                      <span className={`ml-2 text-sm font-medium ${
                        tokenInfo.isExpired === false ? 'text-green-600' :
                        tokenInfo.isExpired === true ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {tokenInfo.isExpired === false ? '✅ Valid' :
                         tokenInfo.isExpired === true ? '❌ Expired' :
                         '⚠️ No expiration'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <h4 className="text-sm font-medium text-blue-700 mb-2">Timestamps</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                      <div>
                        <span className="font-medium text-blue-700">Issued At:</span>
                        <div className="text-blue-600">{formatTimestamp(tokenInfo.issued)}</div>
                      </div>
                      <div>
                        <span className="font-medium text-blue-700">Expires At:</span>
                        <div className="text-blue-600">{formatTimestamp(tokenInfo.expires)}</div>
                      </div>
                      <div>
                        <span className="font-medium text-blue-700">Not Before:</span>
                        <div className="text-blue-600">{formatTimestamp(tokenInfo.notBefore)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {decodedResult && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Header
                      </label>
                      <button
                        onClick={() => setIsFullScreenOpen(true)}
                        className="inline-flex items-center px-3 py-1.5 text-xs bg-stone-600 text-white rounded-md hover:bg-stone-700 transition-colors duration-200"
                        title="Expand to full screen"
                      >
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        Expand
                      </button>
                    </div>
                    <textarea
                      value={JSON.stringify(decodedResult.header, null, 2)}
                      readOnly
                      className="w-full h-[480px] px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-gray-50 resize-none font-mono text-sm"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Payload
                      </label>
                    </div>
                    <textarea
                      value={JSON.stringify(decodedResult.payload, null, 2)}
                      readOnly
                      className="w-full h-[480px] px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-gray-50 resize-none font-mono text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Header (JSON)
                  </label>
                  <textarea
                    value={headerInput}
                    onChange={(e) => setHeaderInput(e.target.value)}
                    placeholder='{"alg": "HS256", "typ": "JWT"}'
                    className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payload (JSON)
                  </label>
                  <textarea
                    value={payloadInput}
                    onChange={(e) => setPayloadInput(e.target.value)}
                    placeholder='{"sub": "1234567890", "name": "John Doe", "iat": 1516239022}'
                    className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                  />
                </div>
              </div>

              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secret (Optional - for demo signature)
                </label>
                <input
                  type="text"
                  value={secretInput}
                  onChange={(e) => setSecretInput(e.target.value)}
                  placeholder="your-256-bit-secret"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ⚠️ This is for demonstration only. Do not use in production.
                </p>
              </div>

              <div className="text-center mt-6">
                <button
                  onClick={handleEncodeJWT}
                  className="px-6 py-3 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm"
                >
                  🔒 Generate JWT
                </button>
              </div>

              {encodedResult && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Generated JWT Token
                    </label>
                    <button
                      onClick={() => setIsFullScreenOpen(true)}
                      className="inline-flex items-center px-3 py-1.5 text-xs bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200"
                      title="Expand to full screen"
                    >
                      <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      Expand
                    </button>
                  </div>
                  <textarea
                    value={encodedResult}
                    readOnly
                    className="w-full h-32 px-4 py-3 border border-green-300 rounded-lg shadow-sm bg-green-50 resize-none font-mono text-sm"
                  />
                  <div className="text-center mt-2">
                    <p className="text-gray-500 text-xs">
                      {encodedResult.length} characters
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 text-center">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto">
              <h3 className="text-yellow-800 font-medium mb-2">🔒 Security Notice</h3>
              <p className="text-yellow-700 text-sm">
                This tool is for educational and development purposes. The encoding feature creates demo tokens with simulated signatures. 
                For production use, always use proper cryptographic libraries and never expose secrets in client-side code.
              </p>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        title={`JWT Tool - ${mode === 'decode' ? 'Decoded Token' : 'Generated Token'}`}
        copyText={getOutputContent()}
        content={
          <textarea
            value={getOutputContent()}
            readOnly
            className="w-full h-full resize-none font-mono text-sm p-4 border-none focus:outline-none bg-gray-50"
          />
        }
      />

      <footer className="py-6 text-center">
        <p className="text-xs text-gray-500">
          Decode JWT tokens to inspect headers and payloads • Encode for testing purposes
        </p>
      </footer>
    </div>
  );
};

// Base64 Tool Component with Full Screen Support
const Base64Tool = () => {
  const [mode, setMode] = useState('encode');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [outputInfo, setOutputInfo] = useState(null);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const sampleText = "Hello, World! This is a sample text for Base64 encoding. It includes special characters: !@#$%^&*()_+{}|:<>?[];',./`~";
  const sampleBase64 = "SGVsbG8sIFdvcmxkISBUaGlzIGlzIGEgc2FtcGxlIHRleHQgZm9yIEJhc2U2NCBlbmNvZGluZy4gSXQgaW5jbHVkZXMgc3BlY2lhbCBjaGFyYWN0ZXJzOiAhQCMkJV4mKigpXyt7fXw6PD4/W107JywuL2B+";

  const encodeBase64 = (text) => {
    if (!text) {
      setOutput('');
      setOutputInfo(null);
      setError('');
      return;
    }

    try {
      const encoded = btoa(unescape(encodeURIComponent(text)));
      setOutput(encoded);
      setError('');
      
      setOutputInfo({
        originalLength: text.length,
        encodedLength: encoded.length,
        compressionRatio: ((encoded.length / text.length) * 100).toFixed(1),
        hasPadding: encoded.endsWith('=') || encoded.endsWith('=='),
        paddingCount: encoded.endsWith('==') ? 2 : encoded.endsWith('=') ? 1 : 0
      });
    } catch (err) {
      setError('Failed to encode: ' + err.message);
      setOutput('');
      setOutputInfo(null);
    }
  };

  const decodeBase64 = (base64) => {
    if (!base64) {
      setOutput('');
      setOutputInfo(null);
      setError('');
      return;
    }

    try {
      const cleanBase64 = base64.replace(/\s/g, '');
      
      if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleanBase64)) {
        throw new Error('Invalid Base64 format. Only A-Z, a-z, 0-9, +, /, and = characters are allowed.');
      }

      const decoded = decodeURIComponent(escape(atob(cleanBase64)));
      setOutput(decoded);
      setError('');
      
      setOutputInfo({
        originalLength: cleanBase64.length,
        decodedLength: decoded.length,
        compressionRatio: ((cleanBase64.length / decoded.length) * 100).toFixed(1),
        hasPadding: cleanBase64.endsWith('=') || cleanBase64.endsWith('=='),
        paddingCount: cleanBase64.endsWith('==') ? 2 : cleanBase64.endsWith('=') ? 1 : 0,
        isValidFormat: true
      });
    } catch (err) {
      setError('Failed to decode: ' + err.message);
      setOutput('');
      setOutputInfo(null);
    }
  };

  // Manual processing functions for button clicks
  const handleEncode = () => {
    if (mode === 'encode') {
      encodeBase64(input);
    }
  };

  const handleDecode = () => {
    if (mode === 'decode') {
      decodeBase64(input);
    }
  };

  const loadSample = () => {
    if (mode === 'encode') {
      setInput(sampleText);
    } else {
      setInput(sampleBase64);
    }
    setError('');
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
    setOutputInfo(null);
  };

  const toggleMode = () => {
    const newMode = mode === 'encode' ? 'decode' : 'encode';
    setMode(newMode);
    
    if (output) {
      setInput(output);
      setOutput('');
    } else {
      clearAll();
    }
  };

  const looksLikeBase64 = (text) => {
    if (!text) return false;
    const cleanText = text.replace(/\s/g, '');
    return /^[A-Za-z0-9+/]*={0,2}$/.test(cleanText) && cleanText.length % 4 === 0;
  };

  const suggestedMode = looksLikeBase64(input) ? 'decode' : 'encode';
  const showModeSuggestion = input && suggestedMode !== mode;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="w-full px-4 mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">Base64 Tool</h1>
            <p className="text-gray-600 text-sm">Encode text to Base64 or decode Base64 to text</p>
          </div>

          <div className="flex justify-center mb-6">
            <div className="bg-stone-100 rounded-full p-1 flex">
              <button
                onClick={() => mode !== 'encode' && toggleMode()}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  mode === 'encode'
                    ? 'bg-stone-500 text-white shadow-sm'
                    : 'text-stone-700 hover:text-stone-900'
                }`}
              >
                🔒 Encode Text
              </button>
              <button
                onClick={() => mode !== 'decode' && toggleMode()}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  mode === 'decode'
                    ? 'bg-stone-500 text-white shadow-sm'
                    : 'text-stone-700 hover:text-stone-900'
                }`}
              >
                🔓 Decode Base64
              </button>
            </div>
          </div>

          {showModeSuggestion && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
                <span className="text-yellow-600 text-sm mr-2">💡</span>
                <span className="text-yellow-800 text-sm">
                  This looks like {suggestedMode === 'decode' ? 'Base64 encoded text' : 'plain text'}.
                </span>
                <button
                  onClick={toggleMode}
                  className="ml-2 text-yellow-700 hover:text-yellow-900 underline text-sm"
                >
                  Switch to {suggestedMode}?
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={mode === 'encode' ? handleEncode : handleDecode}
              disabled={!input.trim()}
              className="px-6 py-3 bg-stone-500 text-white rounded-lg hover:bg-stone-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium shadow-sm"
            >
              {mode === 'encode' ? '🔒 Encode to Base64' : '🔓 Decode from Base64'}
            </button>
            <button
              onClick={loadSample}
              className="px-4 py-3 bg-stone-400 text-white rounded-lg hover:bg-stone-500 transition-colors duration-200 text-sm font-medium shadow-sm"
            >
              Load Sample
            </button>
            <button
              onClick={clearAll}
              className="px-4 py-3 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm"
            >
              Clear All
            </button>
          </div>

          {error && (
            <div className="text-center mb-6">
              <p className="text-red-600 text-sm bg-red-50 inline-block px-4 py-2 rounded-lg">
                {error}
              </p>
            </div>
          )}

          <div className="flex gap-6">
            <div className="w-1/4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {mode === 'encode' ? 'Text to Encode' : 'Base64 to Decode'}
                </label>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={mode === 'encode' ? 'Enter text to encode...' : 'Enter Base64 string to decode...'}
                  className="w-full h-[calc(100vh-380px)] px-4 py-3 border border-amber-200 bg-amber-50 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-gray-500 text-xs">
                    {input.length} characters
                    {mode === 'decode' && input && (
                      <span className="ml-2">
                        • {looksLikeBase64(input) ? '✅ Valid Base64 format' : '❌ Invalid Base64 format'}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {mode === 'encode' ? 'Base64 Encoded Result' : 'Decoded Text Result'}
                    {output && (
                      <span className="ml-2 inline-block bg-stone-100 text-stone-800 text-xs px-2 py-1 rounded-full">
                        {mode === 'encode' ? 'Encoded' : 'Decoded'}
                      </span>
                    )}
                  </label>
                  {output && (
                    <button
                      onClick={() => setIsFullScreenOpen(true)}
                      className="inline-flex items-center px-3 py-1.5 text-xs bg-stone-600 text-white rounded-md hover:bg-stone-700 transition-colors duration-200"
                      title="Expand to full screen"
                    >
                      <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      Expand
                    </button>
                  )}
                </div>
                <textarea
                  value={output}
                  readOnly
                  placeholder={mode === 'encode' ? 'Base64 encoded result will appear here...' : 'Decoded text will appear here...'}
                  className="w-full h-[calc(100vh-380px)] px-4 py-3 border border-stone-200 rounded-lg shadow-sm bg-stone-50 resize-none font-mono text-sm"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-gray-500 text-xs">
                    {output.length} characters
                  </p>
                </div>
              </div>
            </div>
          </div>

          {outputInfo && (
            <div className="mt-8">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
                <h3 className="text-blue-800 font-medium mb-3">📊 Processing Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 font-medium">Original:</span>
                    <div className="text-blue-600">{mode === 'encode' ? outputInfo.originalLength : outputInfo.decodedLength} chars</div>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Result:</span>
                    <div className="text-blue-600">{mode === 'encode' ? outputInfo.encodedLength : outputInfo.originalLength} chars</div>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Ratio:</span>
                    <div className="text-blue-600">{outputInfo.compressionRatio}%</div>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Padding:</span>
                    <div className="text-blue-600">{outputInfo.hasPadding ? `${outputInfo.paddingCount}x =` : 'None'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        title={`Base64 Tool - ${mode === 'encode' ? 'Encoded Result' : 'Decoded Result'}`}
        copyText={output}
        content={
          <textarea
            value={output}
            readOnly
            className="w-full h-full resize-none font-mono text-sm p-4 border-none focus:outline-none bg-gray-50"
          />
        }
      />

      <footer className="py-6 text-center">
        <p className="text-xs text-gray-500">
          Encode text to Base64 format or decode Base64 strings back to readable text
        </p>
      </footer>
    </div>
  );
};

// Export all components
export {
  FullScreenModal,
  TextFormatter,
  JSONDiff,
  JWTTool,
  Base64Tool
};